import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparativeAnalysisPanel = ({ selectedPlayers, onClearSelection }) => {
  const calculateValueChange = (history) => {
    if (!history || history?.length < 2) return { change: 0, percentage: 0 };
    const latest = history?.[history?.length - 1]?.value;
    const previous = history?.[0]?.value;
    const change = latest - previous;
    const percentage = ((change / previous) * 100)?.toFixed(1);
    return { change, percentage };
  };

  const getLatestValue = (history) => {
    if (!history || history?.length === 0) return 0;
    return history?.[history?.length - 1]?.value;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="GitCompare" size={20} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-foreground">Comparative Analysis</h3>
            <p className="text-sm text-muted-foreground">Side-by-side player valuation comparison</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onClearSelection}>
          <Icon name="X" size={16} />
          Clear Selection
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {selectedPlayers?.map((playerData, index) => {
          const { change, percentage } = calculateValueChange(playerData?.history);
          const latestValue = getLatestValue(playerData?.history);
          const isPositive = change >= 0;

          return (
            <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="flex items-start gap-3 mb-4">
                {playerData?.player?.profilePhoto ? (
                  <img 
                    src={playerData?.player?.profilePhoto} 
                    alt={playerData?.player?.fullName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground truncate">
                    {playerData?.player?.knownAs || playerData?.player?.fullName}
                  </h4>
                  <p className="text-xs text-muted-foreground">{playerData?.player?.primaryPosition}</p>
                  <p className="text-xs text-muted-foreground">{playerData?.player?.currentClub}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <span className="text-xs text-muted-foreground">Current Value</span>
                  <p className="text-lg font-bold text-foreground">€{(latestValue / 1000000)?.toFixed(1)}M</p>
                </div>

                <div>
                  <span className="text-xs text-muted-foreground">Value Change</span>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={isPositive ? 'TrendingUp' : 'TrendingDown'} 
                      size={16} 
                      className={isPositive ? 'text-success' : 'text-error'}
                    />
                    <p className={`text-sm font-semibold ${isPositive ? 'text-success' : 'text-error'}`}>
                      {isPositive ? '+' : ''}€{(Math.abs(change) / 1000000)?.toFixed(1)}M ({percentage}%)
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-xs text-muted-foreground">Data Points</span>
                  <p className="text-sm font-medium text-foreground">{playerData?.history?.length} valuations</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="font-semibold text-foreground mb-3">Comparison Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-background rounded-lg p-4">
            <span className="text-xs text-muted-foreground">Highest Value</span>
            <p className="text-lg font-bold text-foreground">
              €{(Math.max(...selectedPlayers?.map(p => getLatestValue(p?.history))) / 1000000)?.toFixed(1)}M
            </p>
          </div>
          <div className="bg-background rounded-lg p-4">
            <span className="text-xs text-muted-foreground">Lowest Value</span>
            <p className="text-lg font-bold text-foreground">
              €{(Math.min(...selectedPlayers?.map(p => getLatestValue(p?.history))) / 1000000)?.toFixed(1)}M
            </p>
          </div>
          <div className="bg-background rounded-lg p-4">
            <span className="text-xs text-muted-foreground">Average Value</span>
            <p className="text-lg font-bold text-foreground">
              €{(selectedPlayers?.reduce((sum, p) => sum + getLatestValue(p?.history), 0) / selectedPlayers?.length / 1000000)?.toFixed(1)}M
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparativeAnalysisPanel;